import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { buildCasinoKpiMoneyTypeParam } from "./casinoKpiFilters";

// ─── Filters ────────────────────────────────────────────────────────────────
//
// Shared shape used by both the table and the KPIs endpoint. `provider`,
// `game`, and `state` accept either `undefined` or the sentinel `"all"` to
// mean "no filter" — both are stripped from the wire payload by
// `buildParams` below so the BE sees an absent key (equivalent to "all"
// per the contract).

export type CasinoTopUsersView =
  | "top_winners"
  | "top_losers"
  | "top_turnover";

export type CasinoTopUsersFilters = {
  start_date: string;
  end_date: string;
  view?: CasinoTopUsersView;
  limit?: number;
  provider?: string;
  game?: string;
  state?: string;
  money_type?: string[];
};

const ALL_SENTINEL = "all";

const isAllOrEmpty = (value: string | undefined): boolean =>
  value === undefined || value === "" || value === ALL_SENTINEL;

const buildParams = (
  filters: CasinoTopUsersFilters,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    start_date: filters.start_date,
    end_date: filters.end_date,
  };
  if (filters.view) params.view = filters.view;
  if (typeof filters.limit === "number") params.limit = filters.limit;
  // BE expects bare `<key>=<value>` for single-value filters and treats an
  // absent key (or the literal `"all"`) as "no filter". Mirrors the
  // contract already in use by `useGetSportsbookTopUsers` after PAMENG-2175
  // — the `[eq]` suffix documented elsewhere is a doc-only artefact the
  // BE does not actually parse.
  if (!isAllOrEmpty(filters.provider))
    params["provider"] = filters.provider as string;
  if (!isAllOrEmpty(filters.game)) params["game"] = filters.game as string;
  if (!isAllOrEmpty(filters.state)) params["state"] = filters.state as string;
  Object.assign(params, buildCasinoKpiMoneyTypeParam(filters.money_type));
  return params;
};

// ─── Table endpoint ─────────────────────────────────────────────────────────

export type CasinoTopUserRow = {
  /** Stable key for the table — the raw `user_id` from the BE. */
  key: string;
  userId: string;
  /**
   * Display label. Falls back to `userId` when the BE returns `null` so
   * the cell is never blank (the link still targets `user_id`).
   */
  username: string;
  turnover: number;
  /**
   * Gross Gaming Revenue from the operator's POV (stakes − payouts).
   * Positive = the house won; negative = users net-won against the
   * house. Coloured by sign in the table (mirrors Sportsbook P&L).
   */
  ggr: number;
  marginPct: number;
  rounds: number;
};

export type CasinoTopUsersFilterOption = {
  /** Wire value (e.g. `"pragmaticplay"`, `"CA"`, `25`). */
  value: string | number;
  /** Human label (e.g. `"Pragmatic Play"`, `"California"`, `"25 Users"`). */
  label: string;
};

export type CasinoTopUsersFilterDefinition = {
  /** Filter id from the BE — `view` | `limit` | `provider` | `game` | `state`. */
  id: string;
  options: CasinoTopUsersFilterOption[];
  defaultValues: Array<string | number>;
  selectedValues: Array<string | number>;
};

export type CasinoTopUsersTableData = {
  rows: CasinoTopUserRow[];
  filters: CasinoTopUsersFilterDefinition[];
};

type RawTableRow = {
  user_id?: string | number | null;
  username?: string | null;
  turnover?: number | null;
  ggr?: number | null;
  margin_pct?: number | null;
  rounds?: number | null;
};

type RawFilterOption = {
  value?: string | number | null;
  label?: string | null;
};

type RawFilterDefinition = {
  /** BE uses `key` today; accept `id` too in case the contract changes. */
  key?: string;
  id?: string;
  multi_select?: boolean;
  /**
   * Options can come back as either flat scalars (`["top_winners", …]` —
   * the current BE shape) or as `{value,label}` objects. Both are
   * normalised to the `{value,label}` shape below.
   */
  options?: Array<string | number | RawFilterOption | null | undefined>;
  cascading_options?: unknown;
  default_values?: Array<string | number>;
  selected_values?: Array<string | number>;
};

type RawTableEnvelope = {
  data?: RawTableRow[] | null;
  meta?: { filters?: RawFilterDefinition[] | null } | null;
};

const toNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normaliseFilterOption = (
  raw: string | number | RawFilterOption | null | undefined,
): CasinoTopUsersFilterOption | null => {
  if (raw === null || raw === undefined) return null;
  // Flat scalar: BE uses the same string for both value and label
  // (e.g. `"top_winners"`). Callers can humanise it later if needed.
  if (typeof raw === "string" || typeof raw === "number") {
    return { value: raw, label: String(raw) };
  }
  if (typeof raw === "object" && raw.value !== null && raw.value !== undefined) {
    return {
      value: raw.value as string | number,
      label: String(raw.label ?? raw.value),
    };
  }
  return null;
};

const parseFilterDefinitions = (
  raw: RawFilterDefinition[] | null | undefined,
): CasinoTopUsersFilterDefinition[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      // Filter id arrives as `key` from the BE today. `id` is accepted as
      // a defensive fallback so a future rename doesn't silently drop the
      // entire filter row.
      const id = (entry?.key ?? entry?.id) as string | undefined;
      if (!id) return null;
      const options = Array.isArray(entry.options)
        ? entry.options
            .map(normaliseFilterOption)
            .filter((opt): opt is CasinoTopUsersFilterOption => opt !== null)
        : [];
      return {
        id,
        options,
        defaultValues: Array.isArray(entry.default_values)
          ? entry.default_values
          : [],
        selectedValues: Array.isArray(entry.selected_values)
          ? entry.selected_values
          : [],
      };
    })
    .filter(
      (entry): entry is CasinoTopUsersFilterDefinition => entry !== null,
    );
};

const parseTableResponse = (body: unknown): CasinoTopUsersTableData => {
  if (!isRecord(body)) return { rows: [], filters: [] };

  // Two envelope shapes are accepted so the parser tolerates both the
  // current flat BE response and a future double-envelope variant
  // (matching how other casino endpoints wrap with `{ data: { … } }`):
  //   • Flat (today):   { data: [...rows], meta: { filters: [...] } }
  //   • Nested:         { data: { data: [...rows], meta: { filters: [...] } } }
  let rawRows: RawTableRow[] = [];
  let rawFilters: RawFilterDefinition[] | null | undefined;

  if (Array.isArray(body.data)) {
    rawRows = body.data as RawTableRow[];
    const meta = (body as RawTableEnvelope).meta;
    rawFilters = meta?.filters;
  } else if (isRecord(body.data)) {
    const nested = body.data as RawTableEnvelope;
    if (Array.isArray(nested.data)) {
      rawRows = nested.data;
      rawFilters = nested.meta?.filters;
    }
  }

  const rows: CasinoTopUserRow[] = rawRows
    .filter((row): row is RawTableRow => Boolean(row))
    .map((row, index) => {
      const userId = String(row.user_id ?? "").trim();
      const usernameRaw =
        typeof row.username === "string" ? row.username.trim() : "";
      return {
        // BE always returns a `user_id`, but the parser stays
        // defensive across every other field — the `row-${index}`
        // sentinel keeps React keys unique even if a future payload
        // returns rows with both `user_id` and `username` null,
        // which would otherwise collapse to "" and silently merge
        // rows in the DataTable.
        key: userId || usernameRaw || `row-${index}`,
        userId,
        username: usernameRaw || userId,
        turnover: toNumber(row.turnover),
        ggr: toNumber(row.ggr),
        marginPct: toNumber(row.margin_pct),
        rounds: toNumber(row.rounds),
      };
    });

  return {
    rows,
    filters: parseFilterDefinitions(rawFilters),
  };
};

export const fetchCasinoTopUsers = async (
  filters: CasinoTopUsersFilters,
): Promise<CasinoTopUsersTableData> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.CASINO_TOP_USERS, {
    params: buildParams(filters),
  });
  return parseTableResponse(response?.data);
};

export const useGetCasinoTopUsers = (filters: CasinoTopUsersFilters) => {
  const { start_date, end_date, view, limit, provider, game, state, money_type } =
    filters;

  return useQuery<CasinoTopUsersTableData, Error>({
    // Cache key includes every wire-affecting param. Filters are
    // normalised through `isAllOrEmpty` here so the "all"/"" sentinels
    // collapse into a single cache entry (matching what `buildParams` sends).
    queryKey: [
      "CASINO_TOP_USERS",
      start_date,
      end_date,
      view ?? null,
      limit ?? null,
      isAllOrEmpty(provider) ? null : provider,
      isAllOrEmpty(game) ? null : game,
      isAllOrEmpty(state) ? null : state,
      money_type?.join(",") ?? "",
    ],
    queryFn: () => fetchCasinoTopUsers(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};

// ─── KPIs endpoint ──────────────────────────────────────────────────────────

export type CasinoTopUsersKpiKey =
  | "turnover"
  | "ggr"
  | "margin_pct"
  | "rounds";

export type CasinoTopUsersKpi = {
  /** Top-N aggregate for the current filter set. */
  value: number;
  /** Client-wide aggregate for the same period/filters. */
  total: number;
  /**
   * `value / total * 100` — share of the client-wide total that the
   * top-N accounts for. `null` when the BE can't compute it (e.g.
   * `total === 0`, or the metric is a rate like Margin % where a
   * share-of-total doesn't apply). Single percentage signal surfaced
   * to the wrapper across every tile, per product.
   *
   * Matches the Sportsbook contract. The BE may keep sending
   * `percent_change` for backwards compatibility — the parser
   * intentionally drops it (mirroring the inverse decision we had
   * here before product flipped).
   */
  pctOfTotal: number | null;
};

export type CasinoTopUsersKpisData = Record<
  CasinoTopUsersKpiKey,
  CasinoTopUsersKpi
>;

type RawKpiEntry = {
  value?: number | null;
  total?: number | null;
  /** Share of the client-wide total that top-N accounts for. Surfaced as `pctOfTotal`. */
  pct_of_total?: number | null;
  /**
   * Deviation of top-N from the client-wide total. The BE may still
   * send it for backwards compatibility, but the parser intentionally
   * drops it — `pct_of_total` is the single percentage signal product
   * wants on this report.
   */
  percent_change?: number | null;
};

type RawKpisResponse = {
  data?: Partial<Record<CasinoTopUsersKpiKey, RawKpiEntry | null>> | null;
};

const KPI_KEYS: ReadonlyArray<CasinoTopUsersKpiKey> = [
  "turnover",
  "ggr",
  "margin_pct",
  "rounds",
];

const EMPTY_KPI: CasinoTopUsersKpi = {
  value: 0,
  total: 0,
  pctOfTotal: null,
};

const parseKpisResponse = (body: unknown): CasinoTopUsersKpisData => {
  const envelope = body as RawKpisResponse | null | undefined;
  const data =
    (envelope?.data as
      | Partial<Record<CasinoTopUsersKpiKey, RawKpiEntry | null>>
      | null
      | undefined) ?? null;

  const result = {} as CasinoTopUsersKpisData;
  for (const key of KPI_KEYS) {
    const raw = data?.[key];
    // BE contract: any entry can come back as `null` (the Casino doc
    // explicitly calls this out for `ggr`, and `margin_pct` / `rounds`
    // inherit the same shape). Fall through to the empty placeholder so
    // the tile renders zeros instead of crashing.
    if (!raw || typeof raw !== "object") {
      result[key] = { ...EMPTY_KPI };
      continue;
    }
    result[key] = {
      value: toNumber(raw.value),
      total: toNumber(raw.total),
      // `pct_of_total` is intentionally `null` (not `0`) when the BE
      // can't compute it (e.g. `total === 0`, or the metric is a rate
      // like Margin %). Preserving the distinction lets the wrapper
      // hide the badge instead of showing a misleading "0%".
      pctOfTotal:
        typeof raw.pct_of_total === "number" &&
        Number.isFinite(raw.pct_of_total)
          ? raw.pct_of_total
          : null,
    };
  }
  return result;
};

export const fetchCasinoTopUsersKpis = async (
  filters: CasinoTopUsersFilters,
): Promise<CasinoTopUsersKpisData> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.CASINO_TOP_USERS_KPIS, {
    params: buildParams(filters),
  });
  return parseKpisResponse(response?.data);
};

export const useGetCasinoTopUsersKpis = (filters: CasinoTopUsersFilters) => {
  const { start_date, end_date, view, limit, provider, game, state, money_type } =
    filters;

  return useQuery<CasinoTopUsersKpisData, Error>({
    queryKey: [
      "CASINO_TOP_USERS_KPIS",
      start_date,
      end_date,
      view ?? null,
      limit ?? null,
      isAllOrEmpty(provider) ? null : provider,
      isAllOrEmpty(game) ? null : game,
      isAllOrEmpty(state) ? null : state,
      money_type?.join(",") ?? "",
    ],
    queryFn: () => fetchCasinoTopUsersKpis(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
